/**
 * Piccole manipolazioni di testo che servono solo a schermo.
 *
 * Quello che riguarda *come si scrive un dato* — date, importi, durate, nomi di
 * persona — non sta qui: sta in `@bestehr/shared/format`, perché lo devono dire
 * uguale anche l'API e l'app (§11 del sistema di progetto). Qui restano le tre
 * o quattro operazioni che hanno senso solo dentro un'interfaccia: ricavare
 * delle iniziali da un nome già composto, e confrontare due stringhe come le
 * confronterebbe una persona che cerca.
 */

import { initials } from '@bestehr/shared';

/**
 * Le iniziali di un nome **già composto**.
 *
 * Il deposito della sessione conserva `displayName`, non nome e cognome
 * separati, quindi la versione condivisa — che vuole un oggetto persona — qui
 * non si può chiamare direttamente. Ci si appoggia comunque a quella, invece di
 * riscrivere il taglio: è lì che sta il dettaglio che conta, cioè `Array.from`
 * al posto di `charAt`, senza il quale un cognome che comincia per «È» perde
 * metà carattere.
 *
 * Restituisce `?` quando non c'è niente da cui ricavarle, perché un cerchio
 * vuoto accanto al nome sembra un'immagine che non si è caricata.
 */
export function iniziali(nome: string): string {
  return initials({ displayName: nome });
}

/**
 * La forma di una stringa per confronti «umani».
 *
 * Minuscolo, senza accenti, senza spazi doppi. Serve alla ricerca rapida delle
 * tabelle: chi cerca «perugia» deve trovare «Perugia», e chi cerca «cosi» deve
 * trovare «Cosìmo» — perché sulla tastiera del portatile l'accento è scomodo e
 * nessuno lo digita in una casella di filtro.
 *
 * `NFD` scompone la lettera accentata in lettera + segno, e la classe unicode
 * toglie i segni. È il modo corretto: una tabella di sostituzioni scritta a
 * mano copre `à è ì ò ù` e poi dimentica `ç`, `ñ` e le maiuscole accentate.
 */
export function normalizza(testo: string): string {
  return testo
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    // L'apostrofo tipografico (tastiera del tablet, testo incollato) vale quello dritto
    // dell'anagrafica: «D’Angelo» trova «D'ANGELO». apostrofoDritto, come nell'app.
    .replace(/[\u2018\u2019\u02BC`\u00B4]/g, "'");
}

/**
 * `testo` contiene `cerca`, ignorando maiuscole e accenti.
 *
 * Una ricerca vuota trova tutto: è ciò che rende innocuo svuotare la casella
 * del filtro, invece di ritrovarsi davanti a una tabella vuota.
 */
export function contiene(testo: string | null | undefined, cerca: string): boolean {
  const ago = normalizza(cerca);
  if (ago === '') return true;
  return normalizza(testo ?? '').includes(ago);
}

/**
 * Accorcia a `max` caratteri con i puntini di sospensione, tagliando all'ultima
 * parola intera.
 *
 * Da usare solo quando il testo va accorciato **nel dato** — per esempio in un
 * attributo, o in un messaggio di conferma. Per accorciare a schermo esiste
 * `text-overflow: ellipsis`, che è meglio perché non perde il testo: resta
 * selezionabile, copiabile e leggibile dai lettori di schermo per intero.
 */
export function tronca(testo: string, max: number): string {
  if (testo.length <= max) return testo;
  const tagliato = testo.slice(0, max - 1);
  const spazio = tagliato.lastIndexOf(' ');
  return `${(spazio > max * 0.6 ? tagliato.slice(0, spazio) : tagliato).trimEnd()}…`;
}
